import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { initializeFirestore, connectFirestoreEmulator, doc, getDocFromServer } from 'firebase/firestore';
import firebaseConfig from '../firebase-applet-config.json';

// Non-prod routes the client SDK to the Firebase emulator suite unless the
// operator explicitly opts into hitting real prod from local. This module is
// evaluated in TWO runtimes — the Vite browser bundle AND the Express server
// (imported transitively via shared src/services/*, run under tsx/node) — so
// each runtime gets its own path, and the other path folds/short-circuits away.
let USE_EMULATOR = false;

// Browser/Vite path. Guarding on `import.meta.env.PROD` (a member access Vite
// replaces with a literal) lets the whole `&&` chain fold to a constant `false`
// in the prod build, so `USE_EMULATOR` stays a provably-false const there and the
// `if (USE_EMULATOR)` connect block below is tree-shaken out entirely.
if (import.meta.env?.PROD === false) {
  USE_EMULATOR = import.meta.env.VITE_OI_ALLOW_PROD_FROM_LOCAL !== '1';
}
// Node/server-under-tsx path — this module is imported transitively by the Express
// server (via shared src/services/*). The guard MUST fold away in the browser build
// or the emulator code (and `process`) would ship in the prod bundle. `import.meta.env?.PROD`
// is undefined ONLY under node (Vite replaces it with a literal in both prod and dev
// browser builds, so `=== undefined` is statically false there → esbuild drops this
// block). Keys off the SAME env vars as the server guard in server.ts, so during
// `npm run dev:local` the server-imported client SDK also targets the emulator.
if (
  import.meta.env?.PROD === undefined &&
  typeof process !== 'undefined' &&
  process.env.FIRESTORE_EMULATOR_HOST &&
  process.env.OI_ALLOW_PROD_FROM_LOCAL !== '1'
) {
  USE_EMULATOR = true;
}

// In emulator mode override projectId + the named database id with demo values
// BEFORE initializeApp, so even a misrouted call can't reach gen-lang-client-….
// The database id is forced to (default) — the universally-supported emulator
// path (see the matching server-side fallback in server.ts).
const resolvedConfig = USE_EMULATOR
  ? { ...firebaseConfig, projectId: 'demo-oracle-insight', firestoreDatabaseId: '(default)' }
  : firebaseConfig;

// Initialize Firebase SDK
const app = initializeApp(resolvedConfig);
export const db = initializeFirestore(app, {
  experimentalAutoDetectLongPolling: true
}, resolvedConfig.firestoreDatabaseId);
export const auth = getAuth(app);

if (USE_EMULATOR) {
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  console.log(
    '[Firebase Config] emulator mode → project demo-oracle-insight, ' +
    'Firestore 127.0.0.1:8080, Auth 127.0.0.1:9099'
  );
}

// Validate Connection to Firestore
async function testConnection() {
  try {
    // This is a simple read to verify the connection is active and config is correct
    await getDocFromServer(doc(db, 'test', 'connection'));
  } catch (error) {
    if (error instanceof Error && error.message.includes('the client is offline')) {
      console.error("Firebase Connection Error: The client is offline. Please check your Firebase configuration.");
    }
    // Skip logging for other errors, as this is simply a connection test.
  }
}

// Skip the live prod read against the emulator, which has no seeded
// test/connection document and would only log a false connection error.
if (!USE_EMULATOR) {
  testConnection();
}
